import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import { TriColorGauge } from "../ai/TriColorGauge.tsx";
import styles from "./ManagementDashboard.module.css";

/* ── FG% trend data (12 weeks) ── */
const FG_TREND = [48, 50, 49, 52, 51, 54, 53, 56, 55, 57, 58, 58];
const CHART_W = 280;
const CHART_H = 120;
const CHART_PAD = 20;

function buildTrendLine(data: number[]): { line: string; area: string } {
  const min = Math.min(...data) - 2;
  const max = Math.max(...data) + 2;
  const range = max - min;
  const points = data.map((v, i) => {
    const x = CHART_PAD + (i / (data.length - 1)) * (CHART_W - CHART_PAD * 2);
    const y = CHART_PAD + (1 - (v - min) / range) * (CHART_H - CHART_PAD * 2);
    return { x, y };
  });

  const line = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L${points[points.length - 1].x.toFixed(1)},${CHART_H - CHART_PAD} L${points[0].x.toFixed(1)},${CHART_H - CHART_PAD} Z`;

  return { line, area };
}

const { line: trendLine, area: trendArea } = buildTrendLine(FG_TREND);

/* ── Load status data (names are fixed, labels come from i18n) ── */
const LOAD_STATUS_DATA: { name: string; status: "green" | "yellow" | "red" }[] = [
  { name: "Adebayo", status: "yellow" },
  { name: "Herro", status: "green" },
  { name: "Rozier", status: "yellow" },
  { name: "Wiggins", status: "green" },
  { name: "Jaquez", status: "green" },
  { name: "Powell", status: "green" },
  { name: "Spoelstra", status: "red" },
];

const LOAD_DOT_CLASS: Record<string, string> = {
  green: styles.loadGreen,
  yellow: styles.loadYellow,
  red: styles.loadRed,
};

export function ManagementDashboard() {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();

  const loadLabelMap: Record<string, string> = {
    green: t.aiTeam.managementLoadReady,
    yellow: t.aiTeam.managementLoadMonitor,
    red: t.aiTeam.managementLoadRest,
  };

  const ref = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const panels = el.querySelectorAll("[data-mgmt-panel]");
    gsap.set(panels, { y: 40, opacity: 0, scale: 0.95 });
    gsap.to(panels, {
      y: 0,
      opacity: 1,
      scale: 1,
      duration: 0.7,
      stagger: 0.12,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  return (
    <section ref={ref} className={styles.section}>
      <h2 className={styles.title}>{t.aiTeam.managementTitle}</h2>
      <p className={styles.desc}>{t.aiTeam.managementDesc}</p>

      <div className={styles.grid}>
        {/* Panel 1: FG% Trend */}
        <div className={styles.panel} data-mgmt-panel>
          <div className={styles.panelHeader}>
            <span className={styles.panelIcon}>{t.aiTeam.managementPanels[0].icon}</span>
            <h3 className={styles.panelTitle}>{t.aiTeam.managementPanels[0].title}</h3>
          </div>
          <p className={styles.panelDesc}>{t.aiTeam.managementPanels[0].desc}</p>
          <div className={styles.chartWrap}>
            <svg
              className={styles.chartSvg}
              viewBox={`0 0 ${CHART_W} ${CHART_H}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="mgmtGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0071e3" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#0071e3" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Grid lines */}
              {[25, 50, 75].map((pct) => (
                <line
                  key={pct}
                  x1={CHART_PAD}
                  y1={CHART_PAD + ((100 - pct) / 100) * (CHART_H - CHART_PAD * 2)}
                  x2={CHART_W - CHART_PAD}
                  y2={CHART_PAD + ((100 - pct) / 100) * (CHART_H - CHART_PAD * 2)}
                  className={styles.chartGrid}
                />
              ))}
              <path d={trendArea} className={styles.chartArea} />
              <path d={trendLine} className={styles.chartLine} />
            </svg>
          </div>
          {/* Performance gauge (reuse TriColorGauge) */}
          <div className={styles.gaugeRow}>
            <div className={styles.gaugeWrap}>
              <TriColorGauge
                segments={{ ext1: 5, warn1: 15, ok: 58, warn2: 15, ext2: 7 }}
                label={t.aiTeam.managementGaugeLabel}
                leftLabel={t.aiTeam.managementGaugeLeft}
                rightLabel={t.aiTeam.managementGaugeRight}
              />
            </div>
          </div>
        </div>

        {/* Panel 2: Load Status */}
        <div className={styles.panel} data-mgmt-panel>
          <div className={styles.panelHeader}>
            <span className={styles.panelIcon}>{t.aiTeam.managementPanels[1].icon}</span>
            <h3 className={styles.panelTitle}>{t.aiTeam.managementPanels[1].title}</h3>
          </div>
          <p className={styles.panelDesc}>{t.aiTeam.managementPanels[1].desc}</p>
          <div className={styles.loadList}>
            {LOAD_STATUS_DATA.map((player) => (
              <div key={player.name} className={styles.loadItem}>
                <span className={`${styles.loadDot} ${LOAD_DOT_CLASS[player.status]}`} />
                <span className={styles.loadName}>{player.name}</span>
                <span className={styles.loadLabel}>{loadLabelMap[player.status]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Panel 3: Weekly Schedule */}
        <div className={styles.panel} data-mgmt-panel>
          <div className={styles.panelHeader}>
            <span className={styles.panelIcon}>{t.aiTeam.managementPanels[2].icon}</span>
            <h3 className={styles.panelTitle}>{t.aiTeam.managementPanels[2].title}</h3>
          </div>
          <p className={styles.panelDesc}>{t.aiTeam.managementPanels[2].desc}</p>
          <div className={styles.schedList}>
            {t.aiTeam.managementSchedule.map((item) => (
              <div key={item.day} className={styles.schedItem}>
                <span className={styles.schedIcon}>{item.icon}</span>
                <span className={styles.schedText}>{item.text}</span>
                <span className={styles.schedDay}>{item.day}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Panel 4: AI Insights */}
        <div className={styles.panel} data-mgmt-panel>
          <div className={styles.panelHeader}>
            <span className={styles.panelIcon}>{t.aiTeam.managementPanels[3].icon}</span>
            <h3 className={styles.panelTitle}>{t.aiTeam.managementPanels[3].title}</h3>
          </div>
          <p className={styles.panelDesc}>{t.aiTeam.managementPanels[3].desc}</p>
          <div className={styles.insightList}>
            {t.aiTeam.managementInsights.map((insight) => (
              <div
                key={insight.text}
                className={`${styles.insightCard} ${
                  insight.type === "warning" ? styles.insightWarning : styles.insightSuggestion
                }`}
              >
                {insight.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
